import { z } from "zod";
import { TimeSlot } from "@carry/shared";
import { PublicIdSchema } from "./common";

/** 提案の業務状態 (draft→adjusted→confirmed→executed)。最適化ジョブ状態とは別軸。 */
export const RouteProposalStatus = z.enum(["draft", "adjusted", "confirmed", "executed"]);

/** 最適化ジョブ状態 (queued→running→completed/failed)。SSE はこの状態で進捗・完了・失敗を判定する。 */
export const RouteProposalJobStatus = z.enum(["queued", "running", "completed", "failed"]);

export const OptimizationOptions = z.object({
  /**
   * ソルバーの上限時間 (秒)。実際は入力サイズに応じて自動調整され、
   * 改善停止検出により更に早く完了する。requested は上限としてのみ使う。
   */
  time_limit_seconds: z.number().int().min(5).max(1800).default(60),
  enable_cross_dock: z.boolean().default(true),
  /** pickup → 次 stop の実走時間に掛ける係数 (省略時は system_settings 値). */
  pickup_travel_multiplier: z.number().min(0).optional(),
  /** pickup → 次 stop の実走時間に加算する余裕分 (秒, 省略時は system_settings 値). */
  pickup_travel_buffer_seconds: z.number().int().min(0).optional(),
  /** delivery → 次 stop の実走時間に掛ける係数 (省略時は system_settings 値). */
  delivery_travel_multiplier: z.number().min(0).optional(),
  /** delivery → 次 stop の実走時間に加算する余裕分 (秒, 省略時は system_settings 値). */
  delivery_travel_buffer_seconds: z.number().int().min(0).optional(),
});

/**
 * 最適化の入力段階で起きた「無言だと気づけない」事象 (#544)。
 * - orders_truncated:             当日の受注が上限を超え、超過分を solver へ渡していない
 * - delivery_window_clamped:      指定時間の端が便区分窓の外に出ていたため切り詰めた
 * - pickup_window_narrowed_by_spot: 集荷指定が集荷拠点マスタの集荷可能時刻からはみ出し、交差まで狭めた (#614)
 * - pickup_window_outside_spot:   集荷指定が集荷可能時刻と全く交差せず、集荷可能時刻を採用した (#614)
 * - pickup_window_spot_ignored:   集荷可能時刻を適用すると便区分の時間帯で集荷できなくなるため、
 *                                 集荷可能時刻を使わず受注の集荷指定で最適化した (#614)
 * - orders_on_locked_routes:      確定済みレーンに載っている注文を最適化対象から除外した (#614)
 * - delivery_window_one_sided:    配達指定が片側だけのため、空いている側を便区分窓の端まで広げた (#677)
 * - pickup_window_one_sided:      集荷指定が片側だけのため、空いている側を便区分窓の端まで広げた (#677)
 *
 * 以下は入力でなく**結果**の警告 (#856)。ジョブ完了時に追記する (配車担当への伝え方が同じなので同じ枠に載せる)。
 * - drivers_unused:               選択した出勤ドライバーのうちルートを作れなかった人がいる
 * - workload_imbalance:           件数・拘束時間の偏りが目安を超えて残った
 */
export const OptimizationInputWarningCode = z.enum([
  "orders_truncated",
  "delivery_window_clamped",
  "pickup_window_narrowed_by_spot",
  "pickup_window_outside_spot",
  "pickup_window_spot_ignored",
  "orders_on_locked_routes",
  "delivery_window_one_sided",
  "pickup_window_one_sided",
  "drivers_unused",
  "workload_imbalance",
]);

export const OptimizationInputWarning = z.object({
  code: OptimizationInputWarningCode,
  /** 配車担当に見せる説明文 (件数・対処を含む)。 */
  message: z.string(),
  /** 該当件数。order_public_ids はサンプル (先頭数件) なので、全体件数はこちらを見る。 */
  count: z.number().int(),
  order_public_ids: z.array(z.string()),
});

export const RouteProposal = z.object({
  id: PublicIdSchema,
  delivery_plan_id: PublicIdSchema,
  /** 提案の業務状態。ジョブ完了後の手動調整〜確定を表す (draft/adjusted/confirmed/executed)。 */
  status: RouteProposalStatus,
  /** 最適化ジョブの実行状態 (queued/running/completed/failed)。進捗・完了・失敗判定はこちらで行う。 */
  job_status: RouteProposalJobStatus,
  calculation_time_ms: z.number().int(),
  total_distance_m: z.number().int(),
  total_time_s: z.number().int(),
  unassigned_order_ids: z.array(z.string()),
  /**
   * 未割当注文の推定理由 (order public_id → 理由文)。
   * OR-Tools はドロップ理由を返さないため、API 層のヒューリスティック診断
   * (スロット窓との不整合・移動時間下限による時間窓不成立の検出) の結果を保持する。
   * 診断導入前の proposal では空オブジェクト。
   */
  unassigned_reasons: z.record(z.string(), z.string()),
  /**
   * 最適化の「入力」に対する警告 (#544)。solver が解けたかどうかとは独立に、
   * 「渡す前に落とした / 指定を狭めた」ことを利用者へ伝える。
   * 警告導入前の proposal では空配列。
   */
  input_warnings: z.array(OptimizationInputWarning),
  solver_version: z.string().nullable(),
  error_message: z.string().nullable(),
  started_at: z.iso.datetime({ offset: true }).nullable(),
  completed_at: z.iso.datetime({ offset: true }).nullable(),
  calculated_at: z.iso.datetime({ offset: true }),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const RunOptimizationInput = z.object({
  plan_public_id: PublicIdSchema,
  // 当日稼働するドライバーを明示指定する (休み等を除外)。未指定時は active driver 全員を対象にする。
  driver_public_ids: z.array(PublicIdSchema).optional(),
  /**
   * ドライバーごとの出勤開始便 (#461)。デポ出発の下限として solver に渡る
   * (早朝から = 制限なし / 午前から = 早朝上限時刻 / 午後から = 午前上限時刻。
   * 境界は system_settings 参照)。未指定のドライバーは制限なし (従来挙動)。
   */
  driver_start_slots: z.record(PublicIdSchema, TimeSlot).optional(),
  /**
   * ドライバーごとの出勤終了便 (#487)。デポ帰着の上限として solver に渡る
   * (早朝まで = 早朝上限時刻 / 午前まで = 午前上限時刻 / 午後まで = 制限なし。
   * 境界は system_settings 参照)。未指定のドライバーは制限なし (従来挙動)。
   */
  driver_end_slots: z.record(PublicIdSchema, TimeSlot).optional(),
  /**
   * ドライバーごとの使用車両指定 (#510)。ダイアログの行ごと車両セレクト由来。
   * 指定されたドライバーは自動割当 (利用車→空きリース車) より優先してこの車両を使う。
   * 未指定のドライバーは従来どおり自動割当。同じ車両を複数の対象ドライバーへ指定した場合と、
   * 稼働中でない車両を指定した場合は 422。
   */
  driver_vehicles: z.record(PublicIdSchema, PublicIdSchema).optional(),
  options: OptimizationOptions.optional(),
});

export const AdjustmentType = z.enum(["reorder", "reassign", "add_stop", "remove_stop"]);

export const AdjustmentReasonCode = z.enum([
  "time_mismatch",
  "delivery_request",
  "driver_preference",
  "traffic",
  "other",
]);

export const AdjustProposalInput = z.object({
  adjustment_type: AdjustmentType,
  reason_code: AdjustmentReasonCode,
  reason_note: z.string().optional(),
  before_json: z.unknown(),
  after_json: z.unknown(),
});

export const ConfirmProposalInput = z.object({
  notify_drivers: z.boolean().default(true),
});

export const OptimizationPublicationLaneStatus = z.enum(["retryable", "published", "skipped"]);

export const OptimizationPublicationLaneReason = z.enum([
  "driver_unassigned",
  "vehicle_unassigned",
  "stale_assignment",
  "route_missing",
  "unexpected_error",
]);

export const OptimizationPublicationBlockedReason = z.enum([
  "plan_terminal",
  "route_missing",
  "proposal_replaced",
  "proposal_not_ready",
]);

export const OptimizationPublicationLane = z.object({
  route_id: PublicIdSchema,
  status: OptimizationPublicationLaneStatus,
  reason: OptimizationPublicationLaneReason.nullable(),
  last_error: z.string().nullable(),
});

export const OptimizationPublication = z.object({
  proposal_id: PublicIdSchema,
  created_at: z.iso.datetime({ offset: true }),
  completed: z.boolean(),
  resume_eligible: z.boolean(),
  blocked_reason: OptimizationPublicationBlockedReason.nullable(),
  published_count: z.number().int().nonnegative(),
  skipped_count: z.number().int().nonnegative(),
  retryable_count: z.number().int().nonnegative(),
  lanes: z.array(OptimizationPublicationLane),
});

export const OptimizationPublicationCandidateList = z.object({
  data: z.array(OptimizationPublication),
});

/** 最適化提案・レーンの公開直前再検証で検出した競合。ID はすべて public_id。 */
export const StaleAssignmentConflict = z.discriminatedUnion("reason", [
  z.object({
    reason: z.literal("order_locked"),
    order_public_id: PublicIdSchema,
    route_public_id: PublicIdSchema,
  }),
  z.object({
    reason: z.literal("driver_locked"),
    driver_public_id: PublicIdSchema,
    route_public_id: PublicIdSchema,
  }),
  z.object({
    reason: z.literal("vehicle_locked"),
    vehicle_public_id: PublicIdSchema,
    route_public_id: PublicIdSchema,
  }),
  z.object({ reason: z.literal("driver_inactive"), driver_public_id: PublicIdSchema }),
  z.object({ reason: z.literal("driver_deleted"), driver_public_id: PublicIdSchema }),
  z.object({ reason: z.literal("vehicle_inactive"), vehicle_public_id: PublicIdSchema }),
  z.object({ reason: z.literal("vehicle_deleted"), vehicle_public_id: PublicIdSchema }),
]);

/** ドライバー選択時の割当車両プレビュー。最適化と同一ロジックで「利用車→空きリース車」を解決する。 */
export const PreviewVehicleAssignmentInput = z.object({
  driver_public_ids: z.array(PublicIdSchema),
});

export const VehicleAssignmentPreviewItem = z.object({
  driver_public_id: PublicIdSchema,
  driver_name: z.string(),
  /** 割当車両。null = 割当可能な稼働車両が無い。 */
  vehicle: z
    .object({
      id: PublicIdSchema,
      vehicle_number: z.string(),
      vehicle_name: z.string().nullable(),
      storage_address: z.string().nullable(),
      /** 保管場所が登録済みか。false の場合は営業所から出発・帰着する。 */
      has_storage_location: z.boolean(),
    })
    .nullable(),
});

export const PreviewVehicleAssignmentOutput = z.object({
  data: z.array(VehicleAssignmentPreviewItem),
});

export type PreviewVehicleAssignmentInputDto = z.infer<typeof PreviewVehicleAssignmentInput>;
export type VehicleAssignmentPreviewItemDto = z.infer<typeof VehicleAssignmentPreviewItem>;

export type RouteProposalDto = z.infer<typeof RouteProposal>;
export type OptimizationInputWarningDto = z.infer<typeof OptimizationInputWarning>;
export type StaleAssignmentConflictDto = z.infer<typeof StaleAssignmentConflict>;
export type OptimizationPublicationDto = z.infer<typeof OptimizationPublication>;
export type OptimizationPublicationLaneDto = z.infer<typeof OptimizationPublicationLane>;
export type OptimizationPublicationBlockedReasonDto = z.infer<
  typeof OptimizationPublicationBlockedReason
>;
